<template>
  <div>
    <div>
      <h2 class="ion-margin-start">
        Unassigned devices
      </h2>

      <ion-badge
        v-for="device in unassignedDevices"
        :key="device.serialNumber"
        class="room-device ion-margin-top ion-margin-end"
        @click="assign(device, null)"
      >
        <ion-spinner
          v-if="device.busy"
          class="small-spinner"
        />
        <template v-else>
          {{ device.serialNumber }}
        </template>
      </ion-badge>
    </div>

    <ion-item
      lines="none"
      class="room-top-bar"
    >
      <ion-item class="search">
        <ion-label position="floating">
          Search
        </ion-label>
        <ion-input-vue v-model="search" />
      </ion-item>

      <ion-button
        slot="end"
        @click="syncEgroDeviceGroups"
      >
        <ion-spinner
          v-if="syncingEgroRooms"
          class="small-spinner"
        />
        <template v-else>
          Sync e-Gro rooms
        </template>
      </ion-button>
      <ion-button
        slot="end"
        @click="add"
      >
        <ion-icon :src="i.add" />
        Create room
      </ion-button>
    </ion-item>

    <v-data-table
      :headers="headers"
      :items="deviceGroups"
      :items-per-page="50"
      :footer-props="{'items-per-page-options':[15, 30, 50, 100, -1]}"
      :search="search"
      :loading="busy"
    >
      <template v-slot:item.devices="{ item }">
        <ion-badge
          v-for="device in item.devices"
          :key="device.serialNumber"
          class="room-device ion-margin-end"
          @click="assign(device, item)"
        >
          <ion-spinner
            v-if="device.busy"
            class="small-spinner"
          />
          <template v-else>
            {{ device.serialNumber }}
          </template>
        </ion-badge>
      </template>

      <template v-slot:item.measurements="{ item }">
        <ion-badge
          v-for="(measurement, index) in item.measurements.recent"
          :key="index"
          :title="measurement.type"
          class="ion-margin-end"
          :class="`sensor-${measurement.type}`"
        >
          {{ measurement | sensorMeasurement }}
        </ion-badge>
      </template>

      <template v-slot:item.slabType="{ item }">
        {{ slabTypeNames[item.slabType] || item.slabType }}
      </template>

      <template v-slot:item.isEgroSynced="{ item }">
        <template v-if="item.isEgroSynced">
          Yes
        </template>
        <template v-else>
          No
        </template>
      </template>

      <template v-slot:item.actions="{ item }">
        <ion-spinner v-if="item.busy" />
        <template v-else>
          <ion-button
            fill="clear"
            @click="edit(item)"
          >
            <ion-icon
              :src="i.pencil"
              color="dark"
            />
          </ion-button>

          <ion-button
            v-if="item.devices.length === 0"
            fill="clear"
            @click="confirmAndDelete(item)"
          >
            <ion-icon
              :src="i.trash"
              color="danger"
            />
          </ion-button>
        </template>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Vue from 'vue'

import { VDataTable } from 'vuetify/lib'
import { IonInputVue } from '@modus/ionic-vue'

import { mapGetters } from 'vuex'

import { alertController, modalController } from '@modus/ionic-vue';

import DeviceGroup from './DeviceGroup'
import Devices from './Devices'
import Device from './Device'
import Delete from './Delete'

import deviceGroupTypes from './deviceGroupTypes'
import slabTypes from './slabTypes'
import { slabTypeNames } from './slabTypes'

export default {
  components: {
    VDataTable,
    IonInputVue
  },
  data() {
    return {
      serialNumber: this.$route.params.serialNumber,
      headers: [
        {
          text: 'Name',
          value: 'name'
        },
        {
          text: 'Type',
          value: 'type'
        },
        {
          text: 'Slab type',
          value: 'slabType'
        },
        {
          text: 'Devices',
          value: 'devices'
        },
        {
          text: 'Recent measurements',
          value: 'measurements',
          sortable: false
        },
        {
          text: 'e-Gro Room',
          value: 'isEgroSynced'
        },
        {
          value: 'actions',
          sortable: false
        }
      ],
      search: '',
      busy: false,
      syncingEgroRooms: false,
      slabTypeNames,
    }
  },
  computed: {
    ...mapGetters([
      'getDetailTerms'
    ]),
    bridge() {
      return this.$store.getters.getBridgeBySerial(this.serialNumber)
    },
    deviceGroupData() {
      return this.bridge.deviceGroupData || {}
    },
    deviceGroups() {
      return this.deviceGroupData.deviceGroups
    },
    unassignedDevices() {
      return this.deviceGroupData.unassignedDevices
    },
    deviceGroup() {
      return this.getDetailTerms('deviceGroup')
    }
  },
  created() {
    const serialNumber = this.serialNumber

    this.busy = true

    this.$store.dispatch('fetchDeviceGroups', { serialNumber })
        .finally(() => this.busy = false)
  },
  methods: {
    confirmAndDelete(deviceGroup) {
      this.confirmDelete(deviceGroup)
        .then(() => this.dispatchDelete(deviceGroup))
    },
    confirmDelete(deviceGroup) {
      return new Promise((resolve, reject) => {
        const room = deviceGroup.name

        alertController
          .create({
            header: `Deleting '${room}'`,
            message: 'Are you sure?',
            buttons: [
              {
                text: 'No',
                role: 'cancel'
              },
              {
                text: 'Yes',
                handler() {
                  resolve()
                }
              }
            ],
          })
          .then(a => a.present())
      })
    },
    dispatchDelete(deviceGroup) {
      const serialNumber = this.$route.params.serialNumber
      const uuid = deviceGroup.uuid

      Vue.set(deviceGroup, 'busy', true)

      this.$store.dispatch('deleteDeviceGroup', { serialNumber, uuid })
        .finally(() => deviceGroup.busy = false)
    },
    add() {
      this.edit({
        type: deviceGroupTypes[0],
        slabType: slabTypes[0]
      })
    },
    edit(deviceGroup) {
      modalController
        .create({
          component: DeviceGroup,
          componentProps: {
            propsData: {
              deviceGroup
            },
            cssClass: 'update-device-group-modal'
          }
        })
        .then(modal => modal.present())
    },
    async assign(device, deviceGroup) {
      const serialNumber = this.serialNumber
      const deviceSerialNumber = device.serialNumber

      device.group = deviceGroup
      Vue.set(device, 'busy', true)

      const modal = await modalController
        .create({
          component: Device,
          componentProps: {
            propsData: {
              serialNumber,
              deviceSerialNumber
            }
          },
          cssClass: 'update-device-modal'
        })

      this.$store.dispatch('fetchDevice', { serialNumber, deviceSerialNumber, device })
        .then(() => modal.present())
        .finally(() => device.busy = false)
    },
    async syncEgroDeviceGroups() {
      this.syncingEgroRooms = true
      await this.$store.dispatch('syncEgroDeviceGroups', { serialNumber: this.serialNumber })
      await new Promise(resolve => setTimeout(resolve, 1000))
      await this.$store.dispatch('syncEgroDevices', { serialNumber: this.serialNumber })
      await new Promise(resolve => setTimeout(resolve, 1000))
      await this.$store.dispatch('fetchDeviceGroups', { serialNumber: this.serialNumber })
      this.syncingEgroRooms = false
    }
  }
}
</script>

<style>
.room-top-bar {
  --padding-start:0;
}

.room-device:hover  {
  cursor:pointer;
}

.room-device ion-spinner {
  width:12px;
  height:12px;
}
</style>
